// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SummaryCard from "../../shared/SummaryCard";
import TrialCard, { TrialEntryType } from "./TrialCard";

export type LevelEntryType = {
  disparityArcsec: number;
  passed?: boolean;
  resolved?: boolean;
  trials: Array<{
    correct: boolean;
    response: string;
    responseTimeSeconds: number;
  }>;
};

type LevelBandType = {
  rung: number;
  levelIndex: number;
  level: LevelEntryType | undefined;
  trials: Array<TrialEntryType>;
  isThreshold: boolean;
};

// One disparity on the ladder: its pass/fail verdict and every trial (rung and catch) that fell
// within it, in the order the patient saw them. A level the run never reached renders as an
// empty placeholder, matching the ladder above which also stops at the first failure.
const LevelBand = ({
  rung,
  level,
  levelIndex,
  trials,
  isThreshold,
}: LevelBandType) => {
  const { t } = useTranslation();

  if (!level) {
    return (
      <SummaryCard
        title={
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
              {rung}″
            </Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
              {t("Not Reached")}
            </Typography>
          </Box>
        }
        cautions={null}
      >
        <Typography fontSize="12px" color="#5B6D7A" pb="8px">
          {t("Not shown. The run stops at the first level the patient fails.")}
        </Typography>
      </SummaryCard>
    );
  }

  const correctCount = level.trials.filter((trial) => trial.correct).length;
  const passed = level.passed ?? correctCount === level.trials.length;
  // A majority of the level's trials passes it: 1 of 1, or 2 of 3 below 200″
  const correctNeeded = Math.floor(level.trials.length / 2) + 1;
  // From algorithm 1.15.0 a level's own trials include its catch trial, because it is scored
  // with the level; before that they held only the real trials
  const catchScored =
    level.trials.length >
    trials.filter((trial) => trial.kind !== "Catch").length;
  let rungTrialCounter = 0;

  return (
    <SummaryCard
      title={
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
            {rung}″{isThreshold && ` - ${t("Threshold")}`}
          </Typography>
          <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
            {passed
              ? t("Passed {{correct}}/{{total}}", {
                  correct: correctCount,
                  total: level.trials.length,
                })
              : t("Failed {{correct}}/{{total}}", {
                  correct: correctCount,
                  total: level.trials.length,
                })}
          </Typography>
        </Box>
      }
      cautions={null}
      disableContentPadding
    >
      {!passed && (
        <Typography fontSize="12px" color="#5B6D7A" p="8px">
          {level.trials.length === 1
            ? t("Missed: the level can't be passed, so the run ends here.")
            : t(
                "{{correct}} of {{total}} correct, short of the {{needed}} needed: the level isn't passed, so the run ends here.",
                {
                  correct: correctCount,
                  total: level.trials.length,
                  needed: correctNeeded,
                }
              )}
        </Typography>
      )}

      {/* Each card grows into whatever its line leaves over, so cards on a line share the width
          equally and a card that wraps onto a line by itself spans the whole card. */}
      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        {trials.map((trial, index) => {
          if (trial.kind !== "Catch") rungTrialCounter += 1;
          return (
            <TrialCard
              key={trial.stepId}
              cardIndex={index}
              levelIndex={levelIndex}
              trial={trial}
              rungTrialNumber={trial.kind === "Catch" ? null : rungTrialCounter}
              catchScored={catchScored}
            />
          );
        })}
      </Box>
    </SummaryCard>
  );
};

export default LevelBand;
